/**
 * Folder picking and PDF enumeration.
 *
 * App-config folder handle is persisted (IndexedDB).
 * Work folder handle is fresh every session (spec §03).
 */

import { saveAppConfigHandle, loadAppConfigHandle, verifyPermission } from './lib/storage.js';

export async function pickAppConfigFolder() {
  if (!('showDirectoryPicker' in window)) {
    throw new Error('This browser does not support showDirectoryPicker(). Use Chrome or Edge.');
  }
  const handle = await window.showDirectoryPicker({ id: 'wiseentry-app-config', mode: 'readwrite' });
  await saveAppConfigHandle(handle);
  return handle;
}

export async function pickWorkFolder() {
  if (!('showDirectoryPicker' in window)) {
    throw new Error('This browser does not support showDirectoryPicker(). Use Chrome or Edge.');
  }
  return await window.showDirectoryPicker({ id: 'wiseentry-work', mode: 'readwrite' });
}

export async function loadStoredAppConfigHandle() {
  return loadAppConfigHandle();
}

export async function ensureReadable(handle)  { return verifyPermission(handle, 'read'); }
export async function ensureWritable(handle) { return verifyPermission(handle, 'readwrite'); }

/**
 * Enumerate .pdf files in a directory handle. Non-PDF files ignored.
 * Returns [{ name, size, handle, file }] sorted by name (numeric-aware).
 */
export async function enumeratePdfs(dirHandle) {
  const out = [];
  for await (const entry of dirHandle.values()) {
    if (entry.kind !== 'file') continue;
    if (!entry.name.toLowerCase().endsWith('.pdf')) continue;
    const file = await entry.getFile();
    out.push({ name: entry.name, size: file.size, handle: entry, file });
  }
  out.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  return out;
}

export function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * List rules / pre-migration JSON files under app-config/backups/ (newest names last).
 */
export async function listRulesBackupFilenames(appConfigRootHandle) {
  const out = [];
  try {
    const dir = await appConfigRootHandle.getDirectoryHandle('backups');
    for await (const [name, entry] of dir.entries()) {
      if (entry.kind !== 'file' || !name.endsWith('.json')) continue;
      if (name.startsWith('rules-') || name.startsWith('pre-migration-')) out.push(name);
    }
  } catch {
    return [];
  }
  out.sort();
  return out;
}
